<template>
  <div class="sus-wrap mt-4">
    <!-- Resumen -->
    <div class="row g-3 mb-3">
      <div class="col-6 col-md-3">
        <div class="sus-stat">
          <div class="sus-stat-label">Coste mensual</div>
          <div class="sus-stat-value">{{ formatMoney(costeMensual) }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="sus-stat">
          <div class="sus-stat-label">Coste anual</div>
          <div class="sus-stat-value">{{ formatMoney(costeMensual * 12) }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="sus-stat">
          <div class="sus-stat-label">Activas</div>
          <div class="sus-stat-value text-success">{{ contadorActivas }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="sus-stat">
          <div class="sus-stat-label">Pausadas</div>
          <div class="sus-stat-value text-warning">{{ contadorPausadas }}</div>
        </div>
      </div>
    </div>

    <!-- Barra de filtros -->
    <div class="sus-toolbar d-flex flex-wrap gap-2 align-items-center mb-3">
      <div class="btn-group btn-group-sm" role="group" aria-label="Filtro de estado">
        <button
          v-for="f in filtros"
          :key="f.valor"
          type="button"
          class="btn"
          :class="filtroEstado === f.valor ? 'btn-primary' : 'btn-outline-primary'"
          @click="filtroEstado = f.valor"
        >
          {{ f.texto }}
        </button>
      </div>
      <button
        class="btn btn-sm position-relative"
        :class="filtroVencimientoActivo ? 'btn-warning' : 'btn-outline-secondary'"
        @click="filtroVencimientoActivo = !filtroVencimientoActivo"
        title="Suscripciones que vencen en el próximo mes"
      >
        <i class="bi bi-calendar-x me-1"></i> Vencen pronto
        <span v-if="contadorVencimientoEnRango > 0"
              class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
          {{ contadorVencimientoEnRango }}
        </span>
      </button>
      <button class="btn btn-sm btn-outline-secondary" @click="mostrarDetalles = !mostrarDetalles">
        <i class="bi" :class="mostrarDetalles ? 'bi-layout-sidebar-reverse' : 'bi-layout-text-sidebar'"></i>
        {{ mostrarDetalles ? 'Vista simple' : 'Vista detallada' }}
      </button>
      <button class="btn btn-sm btn-primary ms-auto" @click="$emit('nuevo')">
        <i class="bi bi-plus-circle me-1"></i> Nueva suscripción
      </button>
    </div>

    <!-- Búsqueda, filtros y orden -->
    <div class="sus-filters row g-2 align-items-end mb-3">
      <div class="col-12 col-md-4">
        <label class="form-label small mb-1">Buscar</label>
        <div class="input-group input-group-sm">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input v-model.trim="busqueda" type="search" class="form-control" placeholder="Concepto u observaciones" />
        </div>
      </div>
      <div class="col-6 col-md-2">
        <label class="form-label small mb-1">Categoría</label>
        <select v-model="filtroCategoria" class="form-select form-select-sm">
          <option value="">Todas</option>
          <option v-for="c in categorias" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>
      <div class="col-3 col-md-1">
        <label class="form-label small mb-1">Mín. €</label>
        <input v-model.number="importeMin" type="number" min="0" step="any" class="form-control form-control-sm" />
      </div>
      <div class="col-3 col-md-1">
        <label class="form-label small mb-1">Máx. €</label>
        <input v-model.number="importeMax" type="number" min="0" step="any" class="form-control form-control-sm" />
      </div>
      <div class="col-8 col-md-2">
        <label class="form-label small mb-1">Ordenar por</label>
        <div class="input-group input-group-sm">
          <select v-model="orden" class="form-select">
            <option v-for="o in opcionesOrden" :key="o.valor" :value="o.valor">{{ o.texto }}</option>
          </select>
          <button type="button" class="btn btn-outline-secondary" :title="ordenAsc ? 'Ascendente' : 'Descendente'"
                  @click="ordenAsc = !ordenAsc">
            <i class="bi" :class="ordenAsc ? 'bi-sort-up' : 'bi-sort-down'"></i>
          </button>
        </div>
      </div>
      <div class="col-4 col-md-2 d-flex gap-2 justify-content-end">
        <div class="btn-group btn-group-sm" role="group" aria-label="Tipo de vista">
          <button type="button" class="btn" :class="vista === 'tarjetas' ? 'btn-primary' : 'btn-outline-primary'"
                  title="Vista de tarjetas" @click="vista = 'tarjetas'"><i class="bi bi-grid-3x2-gap"></i></button>
          <button type="button" class="btn" :class="vista === 'lista' ? 'btn-primary' : 'btn-outline-primary'"
                  title="Vista de lista" @click="vista = 'lista'"><i class="bi bi-list-ul"></i></button>
        </div>
      </div>
    </div>
    <div class="d-flex flex-wrap justify-content-between align-items-center small text-muted mb-2">
      <span>
        {{ suscripcionesFiltradas.length }} de {{ suscripciones.length }} suscripciones ·
        <strong>{{ formatMoney(costeMensualFiltradas) }}</strong>/mes en la selección
      </span>
      <button v-if="hayFiltros" type="button" class="btn btn-link btn-sm p-0" @click="limpiarFiltros">
        <i class="bi bi-x-circle me-1"></i>Limpiar filtros
      </button>
    </div>

    <div v-if="suscripcionesFiltradas.length === 0" class="sus-empty text-center text-muted py-5">
      <i class="bi bi-inbox fs-1 d-block mb-2"></i>
      No hay suscripciones que mostrar.
    </div>

    <!-- Tarjetas -->
    <div v-if="vista === 'tarjetas'" class="row g-3">
      <div v-for="sus in suscripcionesFiltradas" :key="sus.id" class="col-12 col-md-6 col-xl-4">
        <div class="sus-card" :class="{ 'sus-card--pausada': esPausada(sus), 'sus-card--inactiva': !sus.activa }">
          <div class="d-flex justify-content-between align-items-start gap-2">
            <div class="min-w-0">
              <div class="sus-card-title text-truncate" :title="sus.concepto">
                {{ sus.concepto || 'Sin concepto' }}
              </div>
              <div class="sus-card-cat text-muted small">{{ sus.categoria_nombre || 'Sin categoría' }}</div>
            </div>
            <span class="badge" :class="badgeEstado(sus).clase">
              <i class="bi me-1" :class="badgeEstado(sus).icono"></i>{{ badgeEstado(sus).texto }}
            </span>
          </div>

          <div class="sus-card-price my-2">
            {{ formatMoney(sus.cantidad) }}
            <small class="text-muted">/ {{ etiquetaPeriodo(sus.periodicidad) }}</small>
            <span v-if="sus.tipo_division === 'mitad'" class="badge bg-light text-dark border ms-1">Mitad</span>
          </div>

          <dl class="sus-card-meta mb-2">
            <div>
              <dt>Próximo pago</dt>
              <dd>
                {{ esPausada(sus) ? 'En pausa' : formatDate(sus.proximo_pago) }}
                <span v-if="chipDias(sus)" class="badge ms-1" :class="chipDias(sus).clase">{{ chipDias(sus).texto }}</span>
              </dd>
            </div>
            <div>
              <dt>Fin</dt>
              <dd>{{ sus.fecha_fin ? formatDate(sus.fecha_fin) : '∞' }}</dd>
            </div>
            <div>
              <dt>Recordatorio</dt>
              <dd>{{ sus.recordatorio > 0 ? sus.recordatorio + ' días' : '-' }}</dd>
            </div>
            <template v-if="mostrarDetalles">
              <div>
                <dt>Día del mes</dt>
                <dd>{{ sus.dia_mes }}</dd>
              </div>
              <div>
                <dt>Modo de pago</dt>
                <dd>{{ capitalizarPrimeraLetra(sus.modo_pago) || 'Manual' }}</dd>
              </div>
              <div v-if="sus.observaciones" class="sus-meta-wide">
                <dt>Observaciones</dt>
                <dd class="text-wrap">{{ sus.observaciones }}</dd>
              </div>
            </template>
          </dl>

          <div class="sus-card-actions d-flex gap-2">
            <button
              v-if="sus.activa"
              type="button"
              class="btn btn-sm flex-grow-1"
              :class="esPausada(sus) ? 'btn-success' : 'btn-outline-warning'"
              :disabled="cambiandoId === sus.id"
              :title="esPausada(sus) ? 'Reanudar suscripción' : 'Pausar suscripción'"
              @click="togglePausa(sus)"
            >
              <i class="bi me-1" :class="esPausada(sus) ? 'bi-play-fill' : 'bi-pause-fill'"></i>
              {{ esPausada(sus) ? 'Reanudar' : 'Pausar' }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" title="Editar"
                    aria-label="Editar suscripción" @click="$emit('edit', sus)">
              <i class="bi bi-pencil"></i>
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger" title="Eliminar"
                    aria-label="Eliminar suscripción" @click="deleteSuscripcion(sus.id)">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista -->
    <div v-else class="table-responsive sus-lista">
      <table class="table table-sm table-hover align-middle mb-0">
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Categoría</th>
            <th class="text-end">Importe</th>
            <th class="text-end">€/mes</th>
            <th>Próximo pago</th>
            <th>Estado</th>
            <th class="sus-col-acciones">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sus in suscripcionesFiltradas" :key="sus.id" :class="{ 'table-warning': esPausada(sus), 'text-muted': !sus.activa }">
            <td>
              <strong>{{ sus.concepto || 'Sin concepto' }}</strong>
              <div v-if="mostrarDetalles && sus.observaciones" class="small text-muted">{{ sus.observaciones }}</div>
            </td>
            <td>{{ sus.categoria_nombre || '-' }}</td>
            <td class="text-end text-nowrap">{{ formatMoney(sus.cantidad) }} <small class="text-muted">/ {{ etiquetaPeriodo(sus.periodicidad) }}</small></td>
            <td class="text-end text-nowrap">{{ formatMoney(importeMensual(sus)) }}</td>
            <td class="text-nowrap">
              {{ esPausada(sus) ? 'En pausa' : formatDate(sus.proximo_pago) }}
              <span v-if="chipDias(sus)" class="badge ms-1" :class="chipDias(sus).clase">{{ chipDias(sus).texto }}</span>
            </td>
            <td>
              <span class="badge" :class="badgeEstado(sus).clase">
                <i class="bi me-1" :class="badgeEstado(sus).icono"></i>{{ badgeEstado(sus).texto }}
              </span>
            </td>
            <td class="sus-col-acciones">
              <div class="btn-group btn-group-sm" role="group" aria-label="Acciones">
                <button type="button" class="btn" :class="[esPausada(sus) ? 'btn-success' : 'btn-outline-warning', { invisible: !sus.activa }]"
                        :disabled="!sus.activa || cambiandoId === sus.id" :tabindex="sus.activa ? 0 : -1" :aria-hidden="!sus.activa"
                        :title="esPausada(sus) ? 'Reanudar' : 'Pausar'" @click="togglePausa(sus)">
                  <i class="bi" :class="esPausada(sus) ? 'bi-play-fill' : 'bi-pause-fill'"></i>
                </button>
                <button type="button" class="btn btn-outline-secondary" title="Editar" aria-label="Editar suscripción" @click="$emit('edit', sus)">
                  <i class="bi bi-pencil"></i>
                </button>
                <button type="button" class="btn btn-outline-danger" title="Eliminar" aria-label="Eliminar suscripción" @click="deleteSuscripcion(sus.id)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import { ref, computed, watch } from 'vue';

const PERIODOS = { diario: 'día', semanal: 'semana', mensual: 'mes', anual: 'año' };
const FACTOR_MENSUAL = { diario: 30, semanal: 52 / 12, mensual: 1, anual: 1 / 12 };
const PREFS_KEY = 'suscripciones.vista.prefs';

const cargarPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch (e) {
    return {};
  }
};

export default {
  name: 'SuscripcionList',

  props: {
    suscripciones: {
      type: Array,
      required: true
    },
    mostrarTodas: {
      type: Boolean,
      default: false
    }
  },

  emits: ['suscripcion-deleted', 'edit', 'toggle-mostrar-todas', 'nuevo', 'suscripcion-changed'],

  setup(props, { emit }) {
    const mostrarDetalles = ref(false);
    const filtroVencimientoActivo = ref(false);
    const prefs = cargarPrefs();
    const filtroEstado = ref('todas');
    const busqueda = ref('');
    const filtroCategoria = ref('');
    const importeMin = ref(null);
    const importeMax = ref(null);
    const vista = ref(prefs.vista === 'lista' ? 'lista' : 'tarjetas');
    const orden = ref(prefs.orden || 'proximo');
    const ordenAsc = ref(prefs.ordenAsc !== false);

    watch([vista, orden, ordenAsc], () => {
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify({ vista: vista.value, orden: orden.value, ordenAsc: ordenAsc.value }));
      } catch (e) { /* almacenamiento no disponible */ }
    });

    const opcionesOrden = [
      { valor: 'proximo', texto: 'Próximo pago' },
      { valor: 'importe', texto: 'Importe' },
      { valor: 'mensual', texto: 'Coste mensual' },
      { valor: 'nombre', texto: 'Nombre' },
      { valor: 'categoria', texto: 'Categoría' }
    ];

    const categorias = computed(() =>
      [...new Set(props.suscripciones.map(s => s.categoria_nombre).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'))
    );

    const importeMensual = (s) => (Number(s.cantidad) || 0) * (FACTOR_MENSUAL[s.periodicidad] ?? 1);

    const hayFiltros = computed(() =>
      !!(busqueda.value || filtroCategoria.value || importeMin.value || importeMax.value ||
        filtroEstado.value !== 'todas' || filtroVencimientoActivo.value)
    );

    const limpiarFiltros = () => {
      busqueda.value = '';
      filtroCategoria.value = '';
      importeMin.value = null;
      importeMax.value = null;
      filtroEstado.value = 'todas';
      filtroVencimientoActivo.value = false;
    };

    const diasHastaPago = (sus) => {
      if (!sus.activa || sus.pausada || !sus.proximo_pago) return null;
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const f = new Date(sus.proximo_pago);
      f.setHours(0, 0, 0, 0);
      return Math.round((f - hoy) / 86400000);
    };

    const chipDias = (sus) => {
      const d = diasHastaPago(sus);
      if (d === null) return null;
      if (d < 0) return { texto: 'Vencido', clase: 'bg-secondary' };
      if (d === 0) return { texto: 'Hoy', clase: 'bg-danger' };
      if (d <= 7) return { texto: `en ${d} d`, clase: 'bg-warning text-dark' };
      return null;
    };
    const cambiandoId = ref(null);

    const filtros = [
      { valor: 'todas', texto: 'Todas' },
      { valor: 'activas', texto: 'Activas' },
      { valor: 'pausadas', texto: 'Pausadas' },
      { valor: 'inactivas', texto: 'Inactivas' }
    ];

    const esPausada = (sus) => !!sus.activa && !!sus.pausada;

    const badgeEstado = (sus) => {
      if (!sus.activa) return { texto: 'Inactiva', clase: 'bg-danger', icono: 'bi-x-circle' };
      if (sus.pausada) return { texto: 'Pausada', clase: 'bg-warning text-dark', icono: 'bi-pause-circle' };
      return { texto: 'Activa', clase: 'bg-success', icono: 'bi-check-circle' };
    };

    const formatDate = (date) => {
      if (!date) return 'No programado';
      return new Date(date).toLocaleDateString('es-ES');
    };

    const formatMoney = (n) =>
      new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

    const capitalizarPrimeraLetra = (texto) => {
      if (!texto) return '';
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    };

    const etiquetaPeriodo = (p) => PERIODOS[p] || p || 'mes';

    const ordenarPorProximoPago = (lista) => {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      return [...lista].sort((a, b) => {
        const fechaA = a.proximo_pago ? new Date(a.proximo_pago) : null;
        const fechaB = b.proximo_pago ? new Date(b.proximo_pago) : null;

        // Grupos: próximos futuros → pasados → sin fecha; pausadas e inactivas al final
        const grupo = (s, f) => {
          if (!s.activa) return 4;
          if (s.pausada) return 3;
          if (!f) return 2;
          return f < hoy ? 1 : 0;
        };
        const gA = grupo(a, fechaA);
        const gB = grupo(b, fechaB);
        if (gA !== gB) return gA - gB;
        if (!fechaA || !fechaB) return 0;
        return fechaA - fechaB;
      });
    };

    const vencePronto = (sus) => {
      if (!sus.fecha_fin) return false;
      const hoy = new Date();
      const limite = new Date();
      limite.setMonth(hoy.getMonth() + 1);
      const fin = new Date(sus.fecha_fin);
      return fin >= hoy && fin <= limite;
    };

    const suscripcionesFiltradas = computed(() => {
      let lista = props.suscripciones;
      if (filtroEstado.value === 'activas') lista = lista.filter(s => s.activa && !s.pausada);
      else if (filtroEstado.value === 'pausadas') lista = lista.filter(esPausada);
      else if (filtroEstado.value === 'inactivas') lista = lista.filter(s => !s.activa);
      if (filtroVencimientoActivo.value) lista = lista.filter(vencePronto);
      if (filtroCategoria.value) lista = lista.filter(s => s.categoria_nombre === filtroCategoria.value);
      if (typeof importeMin.value === 'number') lista = lista.filter(s => (Number(s.cantidad) || 0) >= importeMin.value);
      if (typeof importeMax.value === 'number') lista = lista.filter(s => (Number(s.cantidad) || 0) <= importeMax.value);
      if (busqueda.value) {
        const q = busqueda.value.toLowerCase();
        lista = lista.filter(s => `${s.concepto || ''} ${s.observaciones || ''}`.toLowerCase().includes(q));
      }

      if (orden.value === 'proximo') {
        const ordenada = ordenarPorProximoPago(lista);
        return ordenAsc.value ? ordenada : ordenada.reverse();
      }
      const dir = ordenAsc.value ? 1 : -1;
      const clave = {
        importe: (s) => Number(s.cantidad) || 0,
        mensual: importeMensual,
        nombre: (s) => (s.concepto || '').toLowerCase(),
        categoria: (s) => (s.categoria_nombre || '').toLowerCase()
      }[orden.value];
      return [...lista].sort((a, b) => {
        const ka = clave(a);
        const kb = clave(b);
        return (typeof ka === 'string' ? ka.localeCompare(kb, 'es') : ka - kb) * dir;
      });
    });

    const costeMensualFiltradas = computed(() =>
      suscripcionesFiltradas.value.filter(s => s.activa && !s.pausada).reduce((acc, s) => acc + importeMensual(s), 0)
    );

    const contadorVencimientoEnRango = computed(() => props.suscripciones.filter(vencePronto).length);
    const contadorActivas = computed(() => props.suscripciones.filter(s => s.activa && !s.pausada).length);
    const contadorPausadas = computed(() => props.suscripciones.filter(esPausada).length);

    // Coste mensual equivalente de las suscripciones en curso (excluye pausadas e inactivas)
    const costeMensual = computed(() =>
      props.suscripciones
        .filter(s => s.activa && !s.pausada)
        .reduce((acc, s) => acc + importeMensual(s), 0)
    );

    const togglePausa = async (sus) => {
      cambiandoId.value = sus.id;
      try {
        await axios.patch(`/api/suscripciones/${sus.id}/pausa`, { pausada: !sus.pausada });
        emit('suscripcion-changed');
      } catch (error) {
        console.error('Error:', error);
        alert('Error al cambiar el estado de la suscripción');
      } finally {
        cambiandoId.value = null;
      }
    };

    const deleteSuscripcion = async (id) => {
      if (!id) {
        alert('Error: No se puede eliminar esta suscripción porque no tiene un ID válido');
        return;
      }
      if (confirm('¿Estás seguro de eliminar esta suscripción?')) {
        try {
          await axios.delete(`/api/suscripciones/${id}`);
          emit('suscripcion-deleted');
        } catch (error) {
          console.error('Error:', error);
          alert('Error al eliminar la suscripción');
        }
      }
    };

    return {
      mostrarDetalles,
      filtroVencimientoActivo,
      filtroEstado,
      busqueda,
      filtroCategoria,
      importeMin,
      importeMax,
      vista,
      orden,
      ordenAsc,
      opcionesOrden,
      categorias,
      importeMensual,
      hayFiltros,
      limpiarFiltros,
      chipDias,
      costeMensualFiltradas,
      filtros,
      cambiandoId,
      esPausada,
      badgeEstado,
      formatDate,
      formatMoney,
      capitalizarPrimeraLetra,
      etiquetaPeriodo,
      suscripcionesFiltradas,
      contadorVencimientoEnRango,
      contadorActivas,
      contadorPausadas,
      costeMensual,
      togglePausa,
      deleteSuscripcion
    };
  }
};
</script>

<style scoped>
.min-w-0 { min-width: 0; }

.sus-stat {
  background: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
}
.sus-stat-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bs-secondary-color, #6c757d);
}
.sus-stat-value {
  font-size: 1.4rem;
  font-weight: 600;
}

.sus-card {
  height: 100%;
  background: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-left: 4px solid var(--bs-success, #198754);
  border-radius: 0.75rem;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease;
}
.sus-card:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); }
.sus-card--pausada { border-left-color: var(--bs-warning, #ffc107); opacity: 0.85; }
.sus-card--inactiva { border-left-color: var(--bs-danger, #dc3545); opacity: 0.65; }

.sus-card-title { font-weight: 600; font-size: 1.05rem; }
.sus-card-price { font-size: 1.5rem; font-weight: 700; }
.sus-card-price small { font-size: 0.8rem; font-weight: 400; }

.sus-card-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}
.sus-card-meta dt {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: var(--bs-secondary-color, #6c757d);
  font-weight: 500;
}
.sus-card-meta dd { margin: 0; font-size: 0.9rem; }
.sus-col-acciones {
  width: 1%;
  white-space: nowrap;
  text-align: center;
}
.sus-meta-wide { grid-column: 1 / -1; }
</style>
